import http from './httpClient';

/**
 * @typedef {Object} Product
 * @property {string} id
 * @property {string} name
 * @property {string} sku
 * @property {string} flavor
 * @property {number} unitSize
 * @property {string} unit
 * @property {number} cost
 * @property {number} price
 * @property {string} updatedAt
 * @property {number} coffee_cost
 * @property {number} ingredient_cost
 * @property {number} can_cost
 * @property {number} packing_cost
 * @property {number} total_cogs
 * @property {number} wholesale_price
 * @property {number} retail_price
 * @property {number} total_wholesale_volume
 * @property {number} coffee_spend
 * @property {number} ingredient_spend
 * @property {boolean} is_active
 * @property {string} notes
 * @property {string} created_at
 * @property {number} wholesale_margin
 * @property {number} retail_margin
 * @property {number} total_wholesale_revenue
 * @property {number} total_wholesale_profit
 * @property {number} currentStock
 * @property {number} minThreshold
 * @property {number} maxCapacity
 */

/**
 * Product API service
 */
const productApi = {
    /**
     * List products with optional query params
     * @param {Object} [params] - Query parameters
     * @param {number} [params.skip=0] - Number of items to skip
     * @param {number} [params.limit=50] - Number of items to return
     * @param {boolean} [params.active_only] - Filter by active status
     * @param {string} [params.flavor] - Filter by flavor name (partial match)
     * @param {string} [params.sort] - Sort field and direction (e.g., 'flavor:asc', 'total_cogs:desc')
     * @param {string} [params.recipe] - Should include recipe details
     * @param {Array<string>} [params.product_ids] - Filter by specific product IDs
     * @returns {Promise<{items: Product[], total: number}>}
     */
    async list(params = {}) {
        // Corrected: Used SEMICOLON (;) to end the declaration, not a comma (,) followed by an extraneous definition block.
        const processedParams = { ...params };
        if (Array.isArray(processedParams.product_ids) && processedParams.product_ids.length === 0) {
            // If empty array, remove the parameter to show all products
            delete processedParams.product_ids;
        }
        const { data } = await http.get('/api/v1/products/', {
            params: processedParams,
            paramsSerializer: params => {
                return Object.entries(params)
                    .map(([key, value]) => {
                        if (Array.isArray(value)) {
                            // Serializes array parameters like product_ids[]=1&product_ids[]=2
                            return value.map(v => `${key}=${encodeURIComponent(v)}`).join('&');
                        }
                        return `${key}=${encodeURIComponent(value)}`;
                    })
                    .join('&');
            }
        });
        return data;
    }, // <--- Comma separating the 'list' method from the next property ('get')

    /**
     * Get a product by id
     * @param {string} id
     * @returns {Promise<Product>}
     */
    async get(id) {
        const { data } = await http.get(`/api/v1/products/${id}`);
        return data;
    },

    /**
     * Create a new product
     * @param {Partial<Product>} payload
     * @returns {Promise<Product>}
     */
    async create(payload) {
        const { data } = await http.post('/api/v1/products/', payload);
        return data;
    },

    /**
     * Update a product
     * @param {string} id
     * @param {Partial<Product>} payload
     * @returns {Promise<Product>}
     */
    async update(id, payload) {
        const { data } = await http.put(`/api/v1/products/${id}`, payload);
        return data;
    },

    /**
     * Delete a product
     * @param {string} id
     * @returns {Promise<void>}
     */
    async remove(id) {
        await http.delete(`/api/v1/products/${id}`);
    },

    /**
     * List available flavors
     * @returns {Promise<string[]>}
     */
    async listFlavors() {
        const { data } = await http.get('/api/v1/products/flavors');
        return data;
    },

    /**
     * Get product analytics (e.g., top performers)
     * @param {Object} params
     * @returns {Promise<any>}
     */
    async analytics(params = {}) {
        const { data } = await http.get('/api/v1/products/analytics', { params });
        return data;
    },

    /**
     * Get product costs
     * @param {Object} params
     * @returns {Promise<any>}
     */
    async getCosts(params = {}) {
        const { data } = await http.get('/api/v1/products/costs', { params });
        return data;
    },

    /**
     * Update product costs (bulk)
     * @param {Array<{id: string, cost: number}>} items
     * @returns {Promise<any>}
     */
    async updateCosts(items) {
        const { data } = await http.put('/api/v1/products/costs', { items });
        return data;
    },

    /**
     * Margin analysis
     * @param {Object} params
     * @returns {Promise<any>}
     */
    async margin(params = {}) {
        const { data } = await http.get('/api/v1/products/margin', { params });
        return data;
    },

    /**
     * Bulk update multiple products
     * @param {Array<{id: string, ...updates}>} items
     * @returns {Promise<any>}
     */
    async bulkUpdate(items) {
        const { data } = await http.put('/api/v1/products/bulk', { items });
        return data;
    }, // <--- Comma separating 'bulkUpdate' from 'getProductionCapacity'

    /**
     * Get production capacity for selected products
     * @param {Array<number>} productIds - Array of product IDs to analyze
     * @param {boolean} includeZeroCapacity - Whether to include products with zero capacity
     * @returns {Promise<any>} Production capacity data with bottleneck analysis
     */
    async getProductionCapacity(productIds = [], includeZeroCapacity = true) {
        const params = {
            include_zero_capacity: includeZeroCapacity
        };

        if (productIds?.length > 0) {
            // Note: This API implementation relies on the custom paramsSerializer to handle arrays correctly.
            params.product_ids = productIds;
        }

        const { data } = await http.get('/api/v1/products/production-capacity', {
            params,
            paramsSerializer: params => {
                return Object.entries(params)
                    .map(([key, value]) => {
                        if (Array.isArray(value)) {
                            return value.map(v => `${key}=${encodeURIComponent(v)}`).join('&');
                        }
                        return `${key}=${encodeURIComponent(value)}`;
                    })
                    .join('&');
            }
        });
        return data;
    } // <--- NO comma here, as this is the final property in the object
}; // <--- Correct final closure of the productApi object

export default productApi;